import { v4 as uuidv4 } from 'uuid';
import { GwDatesWrapper } from '../../../components/_internal_date_/gwDatesWrapper';
import { getStore } from '../../../store/storeRef';
import { DATE_FORMATS, EVENT_CATEGORIES, GTM_EVENT, PRODUCT_SCOPES } from '../../../utilities/constants';
import { pushEvent } from '../../../utilities/dataLayerUtils';
import { getLodgeDataByCode } from '../../../utilities/lodgeData';
import {
  buildPackagesPurchaseEnhancedECommerceObject,
  buildPurchaseEnhancedECommerceObject,
  buildSuiteUpgradeAddToCartEvent,
  buildSuiteUpgradeImpressionsEvent,
  buildSuiteUpgradePurchaseEvent,
  extraGuestPurchaseObj,
  hashEmail
} from './analyticsObjectBuilders';
import { getAddedPackagesCodes, getGroupedAddedPackages, getTotalPackagesRevenueAndTaxes } from './analyticsUtils';

/**
 * Listener triggered on add extra guest
 * @param {Object} eventType The event type
 * @param {number} amount The amount charged on this transaction
 * @param {Object} additionalGuests The object that contains adults and children numbers
 * @param {UUID} UUID Unique ID
 */
export const sendAddExtraGuestInfo = (
  eventType,
  extraGuestAmount,
  extraGuestWithTax,
  extraGuestTax,
  additionalGuests,
  UUID
) => {
  const { entities, suite } = getStore().getState();
  const reservation = entities.manageReservationDetails?.data;

  const addExtraGuestObject = {
    event: eventType,
    resortLocation: reservation.lodge.shortName,
    purchaseLocation: 'modify reservation-add guest',
    ecommerce: {
      currencyCode: 'USD',
      purchase: {
        actionField: {
          id: UUID,
          confirmationNumber: reservation.id,
          revenue: extraGuestWithTax.toFixed(2),
          shipping: '0.00',
          tax: extraGuestTax.toFixed(2),
          list: 'suites',
          position: 1,
          coupon: reservation.rateCode,
          SIP: suite.dayType,
          agent: 'GW'
        },
        products: [extraGuestPurchaseObj(reservation, extraGuestAmount, additionalGuests)]
      }
    }
  };

  pushEvent(addExtraGuestObject);
};
/**
 * Send cabanas post purchase to analytics, post purchase happens when user adds or buys something in CMP
 * @param {object} cabanas Object containing cabanas information to be added from the user
 * @param {object} reservationData Object containing reservation information to be added from the user
 */
export const sendCabanasAnalytics = (cabanas, reservationData, dayType, UUID) => {
  const { duration, guests: guestsReservation, lodge, offerCode, product } = reservationData;

  cabanas?.forEach(cabana => {
    const { code, unitPrice, tax, id, content, type, date } = cabana;
    // Now send enhanced e-commers for transaction
    const cabanaDate = GwDatesWrapper.format(date, DATE_FORMATS.default);
    const locationName = getLodgeDataByCode(lodge?.code).url; //product scope dimension for resort;

    const cabanaPurchaseObj = {
      event: EVENT_CATEGORIES.GTM.transaction,
      resortLocation: locationName,
      purchaseLocation: EVENT_CATEGORIES.GTM.postPurchase,
      ecommerce: {
        currencyCode: 'USD',
        purchase: {
          actionField: {
            id: UUID,
            confirmationNumber: id,
            affiliation: GTM_EVENT.LABEL.greatWolfLowerCase,
            revenue: unitPrice?.toFixed(2),
            shipping: 0,
            tax: tax?.toFixed(2),
            coupon: offerCode,
            SIP: dayType
          },
          products: [
            {
              name: content?.title,
              id: code,
              price: unitPrice,
              brand: GTM_EVENT.LABEL.greatWolfLowerCase,
              category: EVENT_CATEGORIES.GTM.packages,
              variant: type, // used to be rateDescription
              quantity: 1,
              coupon: offerCode,
              dimension29: locationName,
              dimension30: EVENT_CATEGORIES.GTM.packages,
              dimension31: duration, // number of nights
              dimension32: guestsReservation?.adults,
              dimension33: guestsReservation?.children?.length,
              dimension34: EVENT_CATEGORIES.GTM.postPurchase,
              dimension35: cabanaDate,
              dimension36: cabanaDate,
              dimension61: product?.maxOccupancy, // product scope dimension for Maximum Suite Occupancy
              dimension62: guestsReservation?.children || [] // product scope dimension for Kids Ages
            }
          ]
        }
      }
    };
    pushEvent(cabanaPurchaseObj);
  });
};
/**
 *
 * @param {*} packages added packages
 * @param {*} reservation  reservation details
 * @param {*} purchaseLocation  where the purchase is being made
 * @param {*} dayType SIP day type
 * @param {*} maxOccupancy max occupancy of the suite
 */
export const sendPostPurchaseTransaction = async (
  packages,
  reservation,
  purchaseLocation,
  dayType,
  maxOccupancy,
  upsellRecommendationData
) => {
  const addedPackagesCodes = getAddedPackagesCodes(packages);

  const groupedAddedPackages = getGroupedAddedPackages({
    addedPackagesCodes,
    reservation,
    packages
  });

  const [addedPackagesRevenue, addedPackagesTaxes] = getTotalPackagesRevenueAndTaxes({
    addedPackagesCodes,
    groupedAddedPackages
  });

  const transactionPackages = reservation.addOns;

  const packageProducts = buildPackagesPurchaseEnhancedECommerceObject(
    {
      addedPackagesCodes,
      packages: transactionPackages,
      groupedAddedPackages,
      reservation,
      packagesContent: null,
      maxOccupancy,
      upsellRecommendationData
    },
    purchaseLocation || PRODUCT_SCOPES.postPurchase
  );

  const postPurchaseEnhancedECommerce = await buildPurchaseEnhancedECommerceObject(
    {
      reservation,
      products: packageProducts,
      revenue: (addedPackagesRevenue + addedPackagesTaxes).toFixed(2),
      tax: addedPackagesTaxes.toFixed(2),
      shipping: 0
    },
    EVENT_CATEGORIES.GTM.transaction,
    purchaseLocation || PRODUCT_SCOPES.postPurchase
  );

  const postPurchasePackageObj = {
    event: EVENT_CATEGORIES.GTM.postPurchasePackage,
    ecommerce: {
      purchase: {
        actionField: {
          id: reservation.id,
          coupon: reservation?.offerCode || reservation.rateCode,
          revenue: (addedPackagesRevenue + addedPackagesTaxes).toFixed(2),
          SIP: dayType
        },
        products: packageProducts
      }
    }
  };

  pushEvent(postPurchasePackageObj);

  pushEvent(postPurchaseEnhancedECommerce);
};

const pushClickEvent = (click_category, click_type) => {
  pushEvent({
    event: 'custom_click',
    click_category,
    click_type
  });
};
export const MANAGE_RESERVATION_CLICK_TYPES = {
  MANAGE: 'manage reservation',
  ADD_GUEST: 'add guest',
  UPGRADE_SUITE: 'upgrade suite',
  CANCEL: 'cancel reservation'
};
export const sendManageReservationClick = clickType => pushClickEvent('manage reservation', clickType);

/**
 * Fire one product-impressions event for the upgrade offers shown in the drawer.
 * @param {Array} offerGroups Suite upgrade offer groups as returned by the API.
 * @param {Object} reservation The booked reservation (passed by the connected component).
 */
export const sendSuiteUpgradeImpressions = (offerGroups, reservation) => {
  if (!reservation || !offerGroups?.length) return;
  pushEvent(buildSuiteUpgradeImpressionsEvent(offerGroups, reservation));
};

/**
 * Fire an add-to-cart event when the guest selects an upgrade suite.
 * @param {Object} variant The selected offer variant.
 * @param {Object} offerGroup The group the variant belongs to.
 * @param {Object} reservation The booked reservation (passed by the connected component).
 */
export const sendSuiteUpgradeAddToCart = (variant, offerGroup, reservation) => {
  if (!reservation || !variant) return;
  pushEvent(buildSuiteUpgradeAddToCartEvent(variant, offerGroup, reservation));
};

/**
 * Fire the transaction event when a suite upgrade is committed. Tracks the incremental revenue only,
 * taken from the confirmed preview.
 * @param {Object} preview The priced preview the commit was made against.
 * @param {Object} variant The upgraded offer variant.
 * @param {Object} offerGroup The group the variant belongs to.
 * @param {Object} reservation The booked reservation (passed by the connected component).
 */
export const sendSuiteUpgradePurchase = async (preview, variant, offerGroup, reservation) => {
  if (!reservation || !preview) return;
  const email = reservation.contactInfo?.email;
  const emailHash = email ? await hashEmail(email) : '';
  pushEvent(buildSuiteUpgradePurchaseEvent(preview, variant, offerGroup, reservation, uuidv4(), emailHash));
};

export const pushBuyPassAnalyticsEvent = ({ clickType }) => pushClickEvent('buy season pass', clickType);
export const pushFindMySeasonpassEvent = () => pushClickEvent('find my reservation', 'season pass');
export const sendGoToPassesAnalytics = () => pushClickEvent('season pass', 'go to passes');
export const sendPostConfirmAndBookAnalytics = ({ requestObject }) => {
  const {
    locationName,
    daypassId,
    seasonPassId,
    seasonPassCode,
    productName,
    numberOfGuests,
    tax,
    daypassDate,
    code,
    UUID
  } = requestObject;
  const confirmAndBookEvent = {
    event: EVENT_CATEGORIES.GTM.transaction,
    resortLocation: locationName,
    purchaseLocation: EVENT_CATEGORIES.GTM.suiteCheckout,
    ecommerce: {
      currencyCode: 'USD',
      purchase: {
        actionField: {
          id: UUID,
          confirmationNumber: daypassId,
          seasonPassID: seasonPassId,
          affiliation: GTM_EVENT.LABEL.greatWolfLowerCase,
          revenue: 0,
          shipping: 0,
          tax: tax?.toFixed(2),
          coupon: seasonPassCode || '',
          SIP: 0
        },
        products: [
          {
            name: productName,
            id: code,
            price: 0,
            brand: GTM_EVENT.LABEL.greatWolfLowerCase,
            category: EVENT_CATEGORIES.GTM.daypass,
            variant: 'seasonpass redemption', // used to be rateDescription
            quantity: numberOfGuests,
            coupon: seasonPassCode || '',
            dimension29: locationName,
            dimension30: EVENT_CATEGORIES.GTM.daypass,
            dimension31: 0,
            dimension32: 0,
            dimension33: 0,
            dimension34: EVENT_CATEGORIES.GTM.suiteCheckout,
            dimension35: daypassDate,
            dimension36: daypassDate,
            dimension61: 0,
            dimension62: [],
            dimension63: 'Credit Card',
            agent: 'GW'
          }
        ]
      }
    }
  };
  pushEvent(confirmAndBookEvent);
};
export const pushAddToCartDaypassSeasonpassAnalyticsEvent = ({ requestObject }) => {
  const {
    locationName,
    passType,
    productName,
    productId,
    price,
    variant,
    numberOfGuests,
    startDate,
    endDate
  } = requestObject;

  const confirmAndBookEvent = {
    event: EVENT_CATEGORIES.GTM.addToCart,
    resortLocation: locationName,
    purchaseLocation: EVENT_CATEGORIES.GTM.suiteCheckout,
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: {
          list: 'suites',
          position: '',
          coupon: passType
        },
        products: [
          {
            name: productName,
            id: productId,
            price,
            brand: GTM_EVENT.LABEL.greatWolfLowerCase,
            category: EVENT_CATEGORIES.GTM.daypass,
            variant,
            quantity: numberOfGuests,
            coupon: passType,
            dimension27: '',
            dimension28: '',
            dimension29: locationName,
            dimension30: EVENT_CATEGORIES.GTM.daypass,
            dimension31: 0,
            dimension32: 0,
            dimension33: 0,
            dimension34: EVENT_CATEGORIES.GTM.suiteCheckout,
            dimension35: startDate,
            dimension36: endDate,
            dimension61: 0,
            dimension62: [],
            agent: 'GW'
          }
        ]
      }
    }
  };
  pushEvent(confirmAndBookEvent);
};
